import type { Circuit, CircuitEdgeColor, CircuitSurface } from '@multirx/shared/types/track.js';
import { TrackCellType } from '@multirx/shared/types/track.js';
import {
  applyPathCellType,
  countCellTypes,
  countPathSegmentTypes,
  finalizeEditedCircuit,
  getCircuitEditErrorCells,
  isPathClosed,
  validateManualCircuitEdit,
  type PaintableCellType,
} from '@multirx/shared/game/circuitEdit.js';
import {
  createBlankCircuit,
  getPlaceableDrawCells,
  openPathForGeometryEdit,
  tryDrawPathCell,
  undoLastPathCell,
} from '@multirx/shared/game/circuitGeometryEdit.js';
import {
  clearJokerLap,
  getJokerLapPlaceableCells,
  setJokerLapEntry,
  setJokerLapExit,
  tryAppendJokerLapCell,
  undoLastJokerLapCell,
} from '@multirx/shared/game/circuitJokerLapEdit.js';
import {
  clearStartLane,
  getStartLanePlaceableCells,
  setStartLaneJoin,
  tryAppendStartLaneCell,
  undoLastStartLaneCell,
} from '@multirx/shared/game/circuitStartLaneEdit.js';
import { START_LANE_LENGTH } from '@multirx/shared/types/track.js';
import type { SavedCircuit } from './api.js';
import { saveCircuitCopy, updateCircuit } from './api.js';
import {
  addDecorCell,
  fillDecorAroundCircuit,
  getDecorPlaceableCells,
  isDecorCell,
  removeDecorCell,
} from '@multirx/shared/game/circuitDecorEdit.js';
import {
  addDecorSurfaceCell,
  getDecorSurfacePlaceableCells,
  isDecorSurfaceCell,
  removeDecorSurfaceAt,
} from '@multirx/shared/game/circuitDecorSurfaceEdit.js';
import {
  isGrandstandCell,
  isPropRectAnchorCell,
  isSignCell,
  isVehicleCell,
  listPropRectAnchorCells,
} from '@multirx/shared/game/circuitOccupancy.js';
import {
  CIRCUIT_VEHICLE_SPRITES,
  DECOR_VEHICLE_SPRITE_PX,
} from '@multirx/shared/game/circuitVehicleCatalog.js';
import { addCircuitVehicle } from '@multirx/shared/game/circuitVehicleEdit.js';
import {
  addGrandstandRect,
  addSignRect,
  clearAllCircuitAmbience,
  findSignAt,
  removePropAt,
  updateSignText,
} from '@multirx/shared/game/circuitPropEdit.js';
import {
  setCircuitCellEdge,
  setCircuitCellFill,
} from '@multirx/shared/game/circuitSurfaceStyle.js';
import {
  circuitEditorGridHitTest,
  circuitEditorHitTest,
  circuitEditorSurfaceHitTest,
  drawCircuitEditorCanvas,
  getCircuitEditorCanvasSize,
  type CircuitEditorMode,
} from './circuitEditorCanvas.js';
import { preloadCircuitVehicleSprites } from './circuitVehicleSprites.js';
import { createPillButton } from '../ui/pillButton.js';

export interface CircuitEditorHandlers {
  onSaved: (saved: SavedCircuit) => void;
  onCancel: () => void;
}

export type CircuitEditorSource = SavedCircuit & { isNew?: boolean };

const PAINT_TOOLS: { type: PaintableCellType; label: string; hint: string }[] = [
  { type: TrackCellType.STRAIGHT, label: '— Ligne droite', hint: 'Bonus moteur' },
  { type: TrackCellType.TURN, label: '↻ Virage', hint: 'Déplacement / 2' },
  { type: TrackCellType.START, label: '🏁 Départ', hint: 'Bonus pneus' },
];

const MODES: { mode: CircuitEditorMode; label: string; hint: string }[] = [
  { mode: 'trace', label: '✏ Tracer', hint: 'Cliquez pour dessiner le tracé case par case' },
  { mode: 'erase', label: '⌫ Effacer', hint: 'Retire la dernière case du tracé' },
  { mode: 'paint', label: '🎨 Types', hint: 'Assigner ligne droite, virage ou départ' },
  {
    mode: 'joker-lap',
    label: '🔀 Tour joker',
    hint: 'Passage alternatif obligatoire une fois par pilote',
  },
  {
    mode: 'start-lane',
    label: '🏎 Ligne départ',
    hint: `Ligne droite de ${START_LANE_LENGTH} cases rejoignant le circuit (grille multi-voies)`,
  },
  {
    mode: 'surface',
    label: '🎨 Couleurs',
    hint: 'Bitume, terre ou damier sur les cases ; blanc, rouge ou damier sur les bords',
  },
  {
    mode: 'decor',
    label: '🌿 Décor',
    hint: 'Gazon, bitume, terre, gradins, panneaux et véhicules autour du circuit',
  },
];

type DecorKind = 'checker' | 'asphalt' | 'earth' | 'grandstand' | 'sign' | 'vehicle';
type DecorTool = 'paint' | 'erase';

const DECOR_KINDS: { kind: DecorKind; label: string; hint: string }[] = [
  { kind: 'checker', label: '▦ Gazon', hint: 'Cases en damier (gazon) autour du circuit' },
  { kind: 'asphalt', label: '■ Bitume', hint: 'Cases bitumées autour du circuit' },
  { kind: 'earth', label: '■ Terre', hint: 'Cases terre / gravier autour du circuit' },
  { kind: 'grandstand', label: '🏟 Gradins', hint: 'Gradins avec spectateurs sur plusieurs cases' },
  {
    kind: 'sign',
    label: '🪧 Panneau',
    hint: 'Rectangle sur plusieurs cases (comme les gradins), texte personnalisable',
  },
  {
    kind: 'vehicle',
    label: '🚗 Véhicules',
    hint: 'Place un véhicule (sprite fond transparent) sur une case',
  },
];

const DECOR_TOOLS: { tool: DecorTool; label: string; hint: string }[] = [
  { tool: 'paint', label: '+ Placer', hint: 'Ajouter l’élément sélectionné' },
  { tool: 'erase', label: '− Effacer', hint: 'Retirer l’élément sélectionné' },
];

type SurfaceTarget = 'fill' | 'edge';

const SURFACE_TARGETS: { target: SurfaceTarget; label: string; hint: string }[] = [
  { target: 'fill', label: '■ Case', hint: 'Remplir toute la case' },
  { target: 'edge', label: '▢ Bord', hint: 'Colorer le bord le plus proche du clic' },
];

const FILL_COLORS: { color: CircuitSurface | null; label: string; swatch: string; hint: string }[] =
  [
    { color: 'asphalt', label: 'Bitume', swatch: '#334155', hint: 'Surface bitumée' },
    { color: 'earth', label: 'Terre', swatch: '#92400e', hint: 'Surface terre / gravier' },
    {
      color: 'damier',
      label: 'Damier',
      swatch: 'repeating-conic-gradient(#000 0% 25%, #fff 0% 50%) 0 0 / 8px 8px',
      hint: 'Damier noir et blanc',
    },
    { color: null, label: 'Effacer', swatch: 'transparent', hint: 'Retirer la couleur' },
  ];

const EDGE_COLORS: {
  color: CircuitEdgeColor | null;
  label: string;
  swatch: string;
  hint: string;
}[] = [
  { color: 'white', label: 'Blanc', swatch: '#f8fafc', hint: 'Bord blanc' },
  { color: 'red', label: 'Rouge', swatch: '#dc2626', hint: 'Bord rouge' },
  {
    color: 'curb',
    label: 'Rouge / blanc',
    swatch: 'repeating-linear-gradient(90deg, #dc2626 0 50%, #f8fafc 50% 100%)',
    hint: 'Damier rouge et blanc',
  },
  { color: null, label: 'Effacer', swatch: 'transparent', hint: 'Retirer le bord' },
];

function getStartLaneHint(circuit: Circuit): string {
  const sl = circuit.startLane;
  if (!sl || sl.joinPathIndex < 0) {
    return 'Cliquez sur une case du circuit pour le point de jonction (⊕).';
  }
  if (sl.cells.length >= START_LANE_LENGTH) {
    return 'Ligne de départ complète — les pilotes seront répartis sur les 5 cases au départ.';
  }
  if (sl.cells.length === 0) {
    return 'Cases vertes : prolongez la ligne droite depuis le circuit (5 cases au total).';
  }
  return `Tracez la ligne (${sl.cells.length}/${START_LANE_LENGTH}) — restez en ligne droite.`;
}

function getJokerLapHint(circuit: Circuit): string {
  const jl = circuit.jokerLap;
  if (!jl || jl.entryPathIndex < 0) {
    return 'Cliquez sur une case du circuit principal pour l’entrée du tour joker (↪).';
  }
  if (jl.exitPathIndex >= 0) {
    return 'Tour joker terminé. Chaque pilote devra l’emprunter une fois en course.';
  }
  if (jl.cells.length === 0) {
    return 'Cases vertes : cases libres adjacentes à l’entrée. Cliquez pour tracer le passage alternatif (J).';
  }
  return 'Cases vertes : prochaines cases libres. Continuez le tracé ou cliquez la sortie (↩) sur le circuit principal.';
}

/**
 * Départ pré-placé au centre de la grille — laisse de la place pour tourner dans
 * tous les sens. Reproduit exactement ce que fait un vrai premier clic (voir
 * `handleTraceClick` dans `circuit3dEditor.ts`) : placer la case de départ PUIS
 * initialiser la ligne de départ (`setStartLaneJoin`) — sans ce second appel,
 * `draft.startLane` reste `undefined` et l'étape des 5 cases de départ est
 * silencieusement sautée.
 */
function createBlankCircuitCenteredStart(): Circuit {
  const blank = createBlankCircuit();
  const seeded = tryDrawPathCell(blank, Math.floor(blank.width / 2), Math.floor(blank.height / 2));
  const withStart = 'circuit' in seeded ? seeded.circuit : blank;
  return setStartLaneJoin(withStart, 0);
}

export function createManualCircuitDraft(): CircuitEditorSource {
  return {
    id: 0,
    created_at: new Date().toISOString(),
    params: { numStraights: 0, numTurns: 0, approximateCells: 0 },
    circuit: createBlankCircuitCenteredStart(),
    isNew: true,
  };
}

export function mountCircuitEditor(
  container: HTMLElement,
  saved: CircuitEditorSource,
  handlers: CircuitEditorHandlers
): void {
  let draft: Circuit = structuredClone(saved.circuit);
  let editorMode: CircuitEditorMode = saved.isNew ? 'trace' : 'paint';
  let selectedTool: PaintableCellType = TrackCellType.STRAIGHT;
  let surfaceTarget: SurfaceTarget = 'fill';
  let fillColor: CircuitSurface | null = 'asphalt';
  let edgeColor: CircuitEdgeColor | null = 'white';
  let decorKind: DecorKind = 'checker';
  let decorTool: DecorTool = 'paint';
  let propRectAnchor: { x: number; y: number } | null = null;
  let propRectHover: { x: number; y: number } | null = null;
  let signDraftText = '';
  let selectedVehicleSpriteId = CIRCUIT_VEHICLE_SPRITES[0]?.id ?? 'sweeper';
  let statusMessage = saved.isNew
    ? 'Départ placé au centre de la grille — cliquez une case verte/jaune/rouge pour tracer le circuit.'
    : '';
  let statusIsError = false;
  let saving = false;
  let canvasEl: HTMLCanvasElement | null = null;

  const section = document.createElement('div');
  section.className = 'circuits-editor';

  const getEditorPlaceableCells = (): ReadonlyArray<{ x: number; y: number }> => {
    if (editorMode === 'trace') return getPlaceableDrawCells(draft);
    if (editorMode === 'joker-lap') return getJokerLapPlaceableCells(draft);
    if (editorMode === 'start-lane') return getStartLanePlaceableCells(draft);
    if (editorMode === 'decor' && decorTool === 'paint') {
      if (decorKind === 'checker') return getDecorPlaceableCells(draft);
      if (decorKind === 'asphalt' || decorKind === 'earth') {
        return getDecorSurfacePlaceableCells(draft);
      }
      if (decorKind === 'grandstand' || decorKind === 'sign' || decorKind === 'vehicle') {
        return listPropRectAnchorCells(draft);
      }
    }
    return [];
  };

  const paintCanvas = (): void => {
    if (!canvasEl) return;
    drawCircuitEditorCanvas(canvasEl, draft, {
      cellSize: 28,
      padding: 16,
      mode: editorMode,
      errorCells: getCircuitEditErrorCells(draft),
      placeableCells: getEditorPlaceableCells(),
      propPreviewRect: propRectAnchor
        ? {
            x1: propRectAnchor.x,
            y1: propRectAnchor.y,
            x2: propRectHover?.x ?? propRectAnchor.x,
            y2: propRectHover?.y ?? propRectAnchor.y,
          }
        : null,
    });
  };

  const ensureOpenForGeometry = (): void => {
    if ((editorMode === 'trace' || editorMode === 'erase') && isPathClosed(draft.path)) {
      draft = openPathForGeometryEdit(draft);
      statusMessage = 'Circuit rouvert — vous pouvez modifier le tracé.';
      statusIsError = false;
    }
  };

  const render = (): void => {
    ensureOpenForGeometry();
    const validation = validateManualCircuitEdit(draft);
    const errorCells = getCircuitEditErrorCells(draft);
    const cellCounts = countCellTypes(draft.path);
    const segmentCounts = countPathSegmentTypes(draft.path);
    const grandstandCellCount =
      draft.grandstands?.reduce((sum, stand) => sum + stand.cells.length, 0) ?? 0;
    const jokerLapReady = isPathClosed(draft.path);
    const canvasSize = getCircuitEditorCanvasSize(draft, {
      cellSize: 28,
      padding: 16,
      mode: editorMode,
    });
    const errorCoordsText = errorCells.map((cell) => `(${cell.x}, ${cell.y})`).join(', ');
    const pathOpen = draft.path.length > 0 && !isPathClosed(draft.path);

    section.innerHTML = `
      <header class="circuits-editor-header">
        <div>
          <h2 class="circuits-editor-title">${
            saved.isNew ? 'Nouveau circuit' : `Éditeur — Circuit #${saved.id}`
          }</h2>
          <p class="circuits-editor-lead">Tracez le circuit, fermez la boucle sur le départ, puis personnalisez les types de cases. Les nombres saisis à la génération ne limitent pas votre personnalisation.</p>
        </div>
        <div class="circuits-editor-header-actions"></div>
      </header>
      <div class="circuits-editor-body">
        <div class="circuits-editor-main">
          <div class="circuits-editor-modes" role="toolbar" aria-label="Modes d’édition">
            ${MODES.map(
              (item) => `
              <button
                type="button"
                class="circuits-editor-mode${editorMode === item.mode ? ' circuits-editor-mode--active' : ''}"
                data-mode="${item.mode}"
                title="${item.hint}"
              ><span>${item.label}</span></button>`
            ).join('')}
          </div>
          ${
            editorMode === 'paint'
              ? `<div class="circuits-editor-toolbar" role="toolbar" aria-label="Outils de peinture">
                  ${PAINT_TOOLS.map(
                    (tool) => `
                    <button
                      type="button"
                      class="circuits-editor-tool${selectedTool === tool.type ? ' circuits-editor-tool--active' : ''}"
                      data-tool="${tool.type}"
                      title="${tool.hint}"
                    ><span>${tool.label}</span></button>`
                  ).join('')}
                </div>`
              : editorMode === 'trace'
                ? `<p class="circuits-editor-mode-hint">${
                    draft.path.length === 0
                      ? 'Cliquez n’importe où sur la grille pour le départ.'
                      : pathOpen
                        ? 'Cases vertes : prochaines cases possibles. Recliquez le départ (D) pour fermer.'
                        : 'Circuit fermé — passez aux types ou rouvrez avec Effacer.'
                  }</p>`
                : editorMode === 'joker-lap'
                  ? `<p class="circuits-editor-mode-hint">${
                      jokerLapReady
                        ? getJokerLapHint(draft)
                        : 'Fermez d’abord le circuit principal avant d’ajouter un tour joker.'
                    }</p>`
                  : editorMode === 'start-lane'
                    ? `<p class="circuits-editor-mode-hint">${
                        jokerLapReady
                          ? getStartLaneHint(draft)
                          : 'Fermez d’abord le circuit principal avant d’ajouter une ligne de départ.'
                      }</p>`
                    : editorMode === 'surface'
                      ? `<p class="circuits-editor-mode-hint">${
                          jokerLapReady
                            ? surfaceTarget === 'fill'
                              ? 'Cliquez une case du circuit, du tour joker ou de la ligne de départ pour la colorer.'
                              : 'Cliquez près du bord d’une case : blanc, rouge ou damier rouge / blanc.'
                            : 'Fermez d’abord le circuit principal avant de personnaliser les couleurs.'
                        }</p>`
                      : editorMode === 'decor'
                        ? `<p class="circuits-editor-mode-hint">${
                            jokerLapReady
                              ? decorKind === 'checker'
                                ? decorTool === 'paint'
                                  ? 'Cases vertes : cliquez pour ajouter le damier.'
                                  : 'Cliquez une case damier pour la retirer.'
                                : decorKind === 'asphalt'
                                  ? decorTool === 'paint'
                                    ? 'Cases vertes : cliquez pour ajouter du bitume.'
                                    : 'Cliquez une case bitumée pour la retirer.'
                                  : decorKind === 'earth'
                                    ? decorTool === 'paint'
                                      ? 'Cases vertes : cliquez pour ajouter de la terre.'
                                      : 'Cliquez une case terre pour la retirer.'
                                    : decorKind === 'grandstand'
                                      ? decorTool === 'paint'
                                        ? propRectAnchor
                                          ? 'Cliquez le coin opposé pour définir la zone de gradins.'
                                          : 'Cliquez un premier coin, puis le coin opposé (plusieurs cases).'
                                        : 'Cliquez des gradins pour les retirer.'
                                      : decorKind === 'sign'
                                        ? decorTool === 'paint'
                                          ? propRectAnchor
                                            ? 'Cliquez le coin opposé pour définir la zone du panneau.'
                                            : 'Cliquez un premier coin puis le coin opposé (plusieurs cases). Texte optionnel — recliquez un panneau pour le modifier.'
                                          : 'Cliquez un panneau pour le retirer.'
                                        : decorKind === 'vehicle'
                                          ? decorTool === 'paint'
                                            ? 'Choisissez un modèle, puis cliquez une case gazon (le véhicule se pose par-dessus).'
                                            : 'Cliquez un véhicule pour le retirer.'
                                          : ''
                              : 'Fermez d’abord le circuit principal avant d’ajouter le décor.'
                          }</p>`
                        : `<p class="circuits-editor-mode-hint">Cliquez sur le canvas pour retirer la dernière case. Sur un circuit fermé, la boucle est d’abord rouverte.</p>`
          }
          ${
            editorMode === 'decor'
              ? `<div class="circuits-editor-toolbar" role="toolbar" aria-label="Type de décor">
                  ${DECOR_KINDS.map(
                    (item) => `
                    <button
                      type="button"
                      class="circuits-editor-tool${decorKind === item.kind ? ' circuits-editor-tool--active' : ''}"
                      data-decor-kind="${item.kind}"
                      title="${item.hint}"
                    ><span>${item.label}</span></button>`
                  ).join('')}
                </div>
                <div class="circuits-editor-toolbar" role="toolbar" aria-label="Outils décor">
                  ${DECOR_TOOLS.map(
                    (item) => `
                    <button
                      type="button"
                      class="circuits-editor-tool${decorTool === item.tool ? ' circuits-editor-tool--active' : ''}"
                      data-decor-tool="${item.tool}"
                      title="${item.hint}"
                    ><span>${item.label}</span></button>`
                  ).join('')}
                </div>
                ${
                  decorKind === 'sign'
                    ? `<label class="circuits-editor-sign-field">
                        <span>Texte du panneau</span>
                        <input
                          type="text"
                          class="circuits-editor-sign-input"
                          maxlength="60"
                          placeholder="Ex. Zone VIP, Sortie, 50 m…"
                          value="${escapeHtml(signDraftText)}"
                        />
                      </label>`
                    : decorKind === 'vehicle'
                      ? `<div class="circuits-editor-vehicle-picker" role="toolbar" aria-label="Modèle de véhicule">
                          ${CIRCUIT_VEHICLE_SPRITES.map(
                            (sprite) => `
                            <button
                              type="button"
                              class="circuits-editor-vehicle-swatch${
                                selectedVehicleSpriteId === sprite.id
                                  ? ' circuits-editor-vehicle-swatch--active'
                                  : ''
                              }"
                              data-vehicle-sprite="${sprite.id}"
                              title="${escapeHtml(sprite.label)}"
                            >
                              <img src="${sprite.imagePath}" width="${DECOR_VEHICLE_SPRITE_PX}" height="${DECOR_VEHICLE_SPRITE_PX}" alt="" />
                              <span>${escapeHtml(sprite.label)}</span>
                            </button>`
                          ).join('')}
                        </div>
                        <p class="circuits-editor-vehicle-hint">Sprites PNG fond transparent : <code>client/public/assets/circuit-decor/vehicles/</code></p>`
                      : ''
                }
                <div class="circuits-editor-decor-actions"></div>`
              : ''
          }
          ${
            editorMode === 'surface'
              ? `<div class="circuits-editor-toolbar" role="toolbar" aria-label="Cible de coloriage">
                  ${SURFACE_TARGETS.map(
                    (item) => `
                    <button
                      type="button"
                      class="circuits-editor-tool${surfaceTarget === item.target ? ' circuits-editor-tool--active' : ''}"
                      data-surface-target="${item.target}"
                      title="${item.hint}"
                    ><span>${item.label}</span></button>`
                  ).join('')}
                </div>
                <div class="circuits-editor-toolbar" role="toolbar" aria-label="Couleurs">
                  ${(surfaceTarget === 'fill' ? FILL_COLORS : EDGE_COLORS)
                    .map(
                      (item) => `
                    <button
                      type="button"
                      class="circuits-editor-tool circuits-editor-surface-color${
                        (surfaceTarget === 'fill' ? fillColor : edgeColor) === item.color
                          ? ' circuits-editor-tool--active'
                          : ''
                      }${item.color === null ? ' circuits-editor-surface-color--erase' : ''}${
                        item.color === 'curb' ? ' circuits-editor-surface-color--curb' : ''
                      }${item.color === 'damier' ? ' circuits-editor-surface-color--damier' : ''}"
                      data-surface-palette="${surfaceTarget}"
                      data-surface-color="${item.color ?? 'erase'}"
                      title="${item.hint}"
                      style="--surface-swatch: ${item.swatch}"
                    ><span>${item.label}</span></button>`
                    )
                    .join('')}
                </div>`
              : ''
          }
          ${
            draft.jokerLap && draft.jokerLap.entryPathIndex >= 0
              ? `<div class="circuits-editor-joker-actions"></div>`
              : ''
          }
          ${
            draft.startLane && draft.startLane.joinPathIndex >= 0
              ? `<div class="circuits-editor-start-lane-actions"></div>`
              : ''
          }
          <div class="circuits-editor-canvas-wrap">
            <canvas
              class="circuits-editor-canvas"
              width="${canvasSize.width}"
              height="${canvasSize.height}"
              aria-label="Grille du circuit"
            ></canvas>
          </div>
          ${
            errorCells.length > 0
              ? `<p class="circuits-editor-error-legend">Cases en <strong>rouge</strong> (chevauchement, virage trop court ou tracé non fermé) : ${escapeHtml(errorCoordsText)}</p>`
              : ''
          }
        </div>
        <aside class="circuits-editor-side">
          <h3 class="circuits-editor-side-title">Résumé</h3>
          <dl class="circuits-editor-stats">
            <dt>État du tracé</dt><dd>${draft.path.length === 0 ? 'Vide' : isPathClosed(draft.path) ? 'Fermé' : 'Ouvert'}</dd>
            <dt>Segments ligne droite</dt><dd>${segmentCounts.straights}</dd>
            <dt>Segments virage</dt><dd>${segmentCounts.turns}</dd>
            <dt>Cases ligne droite</dt><dd>${cellCounts.straights}</dd>
            <dt>Cases virage</dt><dd>${cellCounts.turns}</dd>
            <dt>Départs</dt><dd>${cellCounts.starts}</dd>
            <dt>Cases du tracé</dt><dd>${draft.path.length}</dd>
            <dt>Tour joker</dt><dd>${
              draft.jokerLap && draft.jokerLap.exitPathIndex >= 0
                ? `${draft.jokerLap.cells.length} cases`
                : draft.jokerLap && draft.jokerLap.entryPathIndex >= 0
                  ? 'En cours'
                  : '—'
            }</dd>
            <dt>Ligne départ</dt><dd>${
              draft.startLane && draft.startLane.cells.length === START_LANE_LENGTH
                ? `${START_LANE_LENGTH} cases`
                : draft.startLane && draft.startLane.joinPathIndex >= 0
                  ? `${draft.startLane.cells.length}/${START_LANE_LENGTH}`
                  : '—'
            }</dd>
            <dt>Décor damier</dt><dd>${draft.decorCells?.length ?? 0} cases</dd>
            <dt>Gradins</dt><dd>${draft.grandstands?.length ?? 0} (${grandstandCellCount} cases)</dd>
            <dt>Panneaux</dt><dd>${draft.signs?.length ?? 0}</dd>
            <dt>Véhicules</dt><dd>${draft.vehicles?.length ?? 0}</dd>
          </dl>
          <div class="circuits-editor-validation${validation.isValid ? ' circuits-editor-validation--ok' : ' circuits-editor-validation--error'}" role="status">
            ${
              validation.isValid
                ? '<p>Circuit prêt à être enregistré.</p>'
                : `<ul>${validation.errors.map((e) => `<li>${escapeHtml(e)}</li>`).join('')}</ul>`
            }
          </div>
          ${
            statusMessage
              ? `<p class="circuits-editor-status${statusIsError ? ' circuits-editor-status--error' : ' circuits-editor-status--ok'}">${escapeHtml(statusMessage)}</p>`
              : ''
          }
          <div class="circuits-editor-actions"></div>
        </aside>
      </div>`;

    canvasEl = section.querySelector('.circuits-editor-canvas');
    paintCanvas();

    section
      .querySelector('.circuits-editor-header-actions')
      ?.appendChild(
        createPillButton('Annuler', { variant: 'soft', small: true, onClick: handlers.onCancel })
      );

    section.querySelector('.circuits-editor-joker-actions')?.appendChild(
      createPillButton('Supprimer le tour joker', {
        variant: 'soft',
        small: true,
        onClick: () => {
          draft = clearJokerLap(draft);
          statusMessage = 'Tour joker supprimé.';
          statusIsError = false;
          render();
        },
      })
    );

    section.querySelector('.circuits-editor-start-lane-actions')?.appendChild(
      createPillButton('Supprimer la ligne de départ', {
        variant: 'soft',
        small: true,
        onClick: () => {
          draft = clearStartLane(draft);
          statusMessage = 'Ligne de départ supprimée.';
          statusIsError = false;
          render();
        },
      })
    );

    const decorActions = section.querySelector('.circuits-editor-decor-actions');
    if (decorActions) {
      decorActions.appendChild(
        createPillButton('Remplir autour', {
          variant: 'soft',
          small: true,
          onClick: () => {
            if (!isPathClosed(draft.path)) return;
            draft = fillDecorAroundCircuit(draft, 1);
            statusMessage = 'Damier ajouté autour du circuit.';
            statusIsError = false;
            render();
          },
        })
      );
      decorActions.appendChild(
        createPillButton('Tout effacer', {
          variant: 'soft',
          small: true,
          onClick: () => {
            draft = clearAllCircuitAmbience(draft);
            propRectAnchor = null;
            propRectHover = null;
            statusMessage = 'Décor supprimé.';
            statusIsError = false;
            render();
          },
        })
      );
    }

    const actionsEl = section.querySelector('.circuits-editor-actions');
    if (actionsEl) {
      const saveBtn = createPillButton(saving ? 'Enregistrement…' : 'Enregistrer', {
        variant: 'cta',
        onClick: () => void persist(false),
      });
      if (!validation.isValid || saving) (saveBtn as HTMLButtonElement).disabled = true;
      actionsEl.appendChild(saveBtn);

      if (!saved.isNew) {
        const copyBtn = createPillButton('Enregistrer une copie', {
          variant: 'soft',
          onClick: () => void persist(true),
        });
        if (!validation.isValid || saving) (copyBtn as HTMLButtonElement).disabled = true;
        actionsEl.appendChild(copyBtn);
      }
    }

    section.querySelectorAll('.circuits-editor-mode').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = (btn as HTMLButtonElement).dataset.mode as CircuitEditorMode | undefined;
        if (
          mode === 'trace' ||
          mode === 'erase' ||
          mode === 'paint' ||
          mode === 'joker-lap' ||
          mode === 'start-lane' ||
          mode === 'surface' ||
          mode === 'decor'
        ) {
          editorMode = mode;
          statusMessage = '';
          statusIsError = false;
          render();
        }
      });
    });

    section.querySelectorAll('.circuits-editor-tool[data-tool]').forEach((btn) => {
      btn.addEventListener('click', () => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- sans le cast, la comparaison ci-dessous devient un no-unsafe-enum-comparison
        const value = Number((btn as HTMLButtonElement).dataset.tool) as PaintableCellType;
        if (
          value === TrackCellType.STRAIGHT ||
          value === TrackCellType.TURN ||
          value === TrackCellType.START
        ) {
          selectedTool = value;
          render();
        }
      });
    });

    section.querySelectorAll('[data-decor-kind]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const kind = (btn as HTMLButtonElement).dataset.decorKind as DecorKind | undefined;
        if (
          kind === 'checker' ||
          kind === 'asphalt' ||
          kind === 'earth' ||
          kind === 'grandstand' ||
          kind === 'sign' ||
          kind === 'vehicle'
        ) {
          decorKind = kind;
          propRectAnchor = null;
          propRectHover = null;
          statusMessage = '';
          statusIsError = false;
          render();
        }
      });
    });

    section.querySelectorAll('[data-decor-tool]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tool = (btn as HTMLButtonElement).dataset.decorTool as DecorTool | undefined;
        if (tool === 'paint' || tool === 'erase') {
          decorTool = tool;
          propRectAnchor = null;
          propRectHover = null;
          render();
        }
      });
    });

    const signInput = section.querySelector<HTMLInputElement>('.circuits-editor-sign-input');
    signInput?.addEventListener('input', () => {
      signDraftText = signInput.value;
    });

    section.querySelectorAll('[data-vehicle-sprite]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const spriteId = (btn as HTMLButtonElement).dataset.vehicleSprite;
        if (!spriteId) return;
        selectedVehicleSpriteId = spriteId;
        render();
      });
    });

    section.querySelectorAll('[data-surface-target]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = (btn as HTMLButtonElement).dataset.surfaceTarget as
          SurfaceTarget | undefined;
        if (target === 'fill' || target === 'edge') {
          surfaceTarget = target;
          render();
        }
      });
    });

    section.querySelectorAll('[data-surface-color]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const palette = (btn as HTMLButtonElement).dataset.surfacePalette;
        const raw = (btn as HTMLButtonElement).dataset.surfaceColor;
        if (palette === 'fill') {
          if (raw === 'erase') fillColor = null;
          else if (raw === 'asphalt' || raw === 'earth' || raw === 'damier') fillColor = raw;
        } else if (palette === 'edge') {
          if (raw === 'erase') edgeColor = null;
          else if (raw === 'white' || raw === 'red' || raw === 'curb') edgeColor = raw;
        }
        render();
      });
    });

    canvasEl?.addEventListener('mousemove', (event) => {
      if (editorMode !== 'decor' || decorTool !== 'paint') return;
      if (decorKind !== 'grandstand' && decorKind !== 'sign') return;
      // véhicules : pas d’aperçu rectangle
      if (!propRectAnchor) {
        if (propRectHover) {
          propRectHover = null;
          paintCanvas();
        }
        return;
      }
      if (!canvasEl) return;
      const grid = circuitEditorGridHitTest(canvasEl, draft, event.clientX, event.clientY, {
        cellSize: 28,
        padding: 16,
        mode: editorMode,
      });
      if (!grid) return;
      if (propRectHover?.x === grid.x && propRectHover?.y === grid.y) return;
      propRectHover = grid;
      paintCanvas();
    });

    canvasEl?.addEventListener('mouseleave', () => {
      if (!propRectHover) return;
      propRectHover = null;
      paintCanvas();
    });

    canvasEl?.addEventListener('click', (event) => {
      if (!canvasEl) return;

      if (editorMode === 'erase') {
        if (draft.startLane && draft.startLane.cells.length > 0) {
          const undoneStart = undoLastStartLaneCell(draft);
          if (undoneStart) {
            draft = undoneStart;
            statusMessage = 'Dernière case de la ligne de départ retirée.';
            statusIsError = false;
            render();
            return;
          }
        }
        if (draft.jokerLap && draft.jokerLap.cells.length > 0) {
          const undoneJoker = undoLastJokerLapCell(draft);
          if (undoneJoker) {
            draft = undoneJoker;
            statusMessage = 'Dernière case du tour joker retirée.';
            statusIsError = false;
            render();
            return;
          }
        }
        const undone = undoLastPathCell(draft);
        if (undone) {
          draft = undone;
          statusMessage = 'Dernière case retirée.';
          statusIsError = false;
        } else {
          statusMessage = 'Rien à effacer.';
          statusIsError = true;
        }
        render();
        return;
      }

      if (editorMode === 'trace') {
        const grid = circuitEditorGridHitTest(canvasEl, draft, event.clientX, event.clientY, {
          cellSize: 28,
          padding: 16,
          mode: editorMode,
        });
        if (!grid) return;
        if (draft.path.length > 0) {
          const allowed = getPlaceableDrawCells(draft);
          const isAllowed = allowed.some((c) => c.x === grid.x && c.y === grid.y);
          if (!isAllowed) return;
        }

        const result = tryDrawPathCell(draft, grid.x, grid.y);
        if ('error' in result) {
          statusMessage = result.error;
          statusIsError = true;
          render();
          return;
        }
        draft = result.circuit;
        statusMessage = result.closed ? 'Circuit fermé — vous pouvez personnaliser les types.' : '';
        statusIsError = false;
        if (result.closed) editorMode = 'paint';
        render();
        return;
      }

      if (editorMode === 'paint') {
        const pathIndex = circuitEditorHitTest(canvasEl, draft, event.clientX, event.clientY, {
          cellSize: 28,
          padding: 16,
          mode: editorMode,
        });
        if (pathIndex === null) return;
        draft = {
          ...draft,
          path: applyPathCellType(draft.path, pathIndex, selectedTool),
        };
        statusMessage = '';
        render();
        return;
      }

      if (editorMode === 'joker-lap') {
        if (!isPathClosed(draft.path)) return;
        const pathIndex = circuitEditorHitTest(canvasEl, draft, event.clientX, event.clientY, {
          cellSize: 28,
          padding: 16,
          mode: editorMode,
        });
        if (pathIndex !== null) {
          const jl = draft.jokerLap;
          if (!jl || jl.entryPathIndex < 0) {
            draft = setJokerLapEntry(draft, pathIndex);
            statusMessage = 'Entrée du tour joker définie — tracez le passage alternatif.';
            statusIsError = false;
            render();
            return;
          }
          if (jl.cells.length > 0) {
            const exitResult = setJokerLapExit(draft, pathIndex);
            if ('circuit' in exitResult) {
              draft = exitResult.circuit;
              statusMessage = 'Tour joker terminé.';
              statusIsError = false;
              render();
              return;
            }
            if ('error' in exitResult) {
              statusMessage = exitResult.error;
              statusIsError = true;
              render();
              return;
            }
          }
        }

        const grid = circuitEditorGridHitTest(canvasEl, draft, event.clientX, event.clientY, {
          cellSize: 28,
          padding: 16,
          mode: editorMode,
        });
        if (!grid || !draft.jokerLap || draft.jokerLap.entryPathIndex < 0) return;
        const allowed = getJokerLapPlaceableCells(draft);
        if (!allowed.some((c) => c.x === grid.x && c.y === grid.y)) return;
        const appendResult = tryAppendJokerLapCell(draft, grid.x, grid.y);
        if ('error' in appendResult) {
          statusMessage = appendResult.error;
          statusIsError = true;
        } else {
          draft = appendResult.circuit;
          statusMessage = 'Case tour joker ajoutée.';
          statusIsError = false;
        }
        render();
        return;
      }

      if (editorMode === 'start-lane') {
        if (!isPathClosed(draft.path)) return;

        const grid = circuitEditorGridHitTest(canvasEl, draft, event.clientX, event.clientY, {
          cellSize: 28,
          padding: 16,
          mode: editorMode,
        });

        if (grid && draft.startLane && draft.startLane.joinPathIndex >= 0) {
          const allowed = getStartLanePlaceableCells(draft);
          if (allowed.some((c) => c.x === grid.x && c.y === grid.y)) {
            const appendResult = tryAppendStartLaneCell(draft, grid.x, grid.y);
            if ('error' in appendResult) {
              statusMessage = appendResult.error;
              statusIsError = true;
            } else {
              draft = appendResult.circuit;
              statusMessage =
                draft.startLane?.cells.length === START_LANE_LENGTH
                  ? 'Ligne de départ complète.'
                  : 'Case de ligne de départ ajoutée.';
              statusIsError = false;
            }
            render();
            return;
          }
        }

        const pathIndex = circuitEditorHitTest(canvasEl, draft, event.clientX, event.clientY, {
          cellSize: 28,
          padding: 16,
          mode: editorMode,
        });
        if (pathIndex !== null && (!draft.startLane || draft.startLane.joinPathIndex < 0)) {
          draft = setStartLaneJoin(draft, pathIndex);
          statusMessage = 'Jonction définie — tracez 5 cases en ligne droite.';
          statusIsError = false;
          render();
          return;
        }

        if (grid) {
          statusMessage = 'Case invalide — cliquez une case verte adjacente à la jonction.';
          statusIsError = true;
          render();
        }
        return;
      }

      if (editorMode === 'surface') {
        if (!isPathClosed(draft.path)) return;
        const hit = circuitEditorSurfaceHitTest(
          canvasEl,
          draft,
          event.clientX,
          event.clientY,
          surfaceTarget,
          { cellSize: 28, padding: 16, mode: editorMode }
        );
        if (!hit) return;
        if (surfaceTarget === 'fill') {
          draft = setCircuitCellFill(draft, hit.x, hit.y, fillColor);
        } else if (hit.edge) {
          draft = setCircuitCellEdge(draft, hit.x, hit.y, hit.edge, edgeColor);
        }
        statusMessage = '';
        statusIsError = false;
        render();
        return;
      }

      if (editorMode === 'decor') {
        if (!isPathClosed(draft.path)) return;
        if (!canvasEl) return;
        const grid = circuitEditorGridHitTest(canvasEl, draft, event.clientX, event.clientY, {
          cellSize: 28,
          padding: 16,
          mode: editorMode,
        });
        if (!grid) return;

        if (decorKind === 'checker') {
          if (decorTool === 'paint') {
            const allowed = getDecorPlaceableCells(draft);
            if (!allowed.some((c) => c.x === grid.x && c.y === grid.y)) return;
            const result = addDecorCell(draft, grid.x, grid.y);
            if ('error' in result) {
              statusMessage = result.error;
              statusIsError = true;
            } else {
              draft = result.circuit;
              statusMessage = '';
              statusIsError = false;
            }
          } else if (isDecorCell(draft, grid.x, grid.y)) {
            draft = removeDecorCell(draft, grid.x, grid.y);
            statusMessage = '';
            statusIsError = false;
          }
          render();
          return;
        }

        if (decorKind === 'asphalt' || decorKind === 'earth') {
          const surface = decorKind === 'asphalt' ? 'asphalt' : 'earth';
          if (decorTool === 'paint') {
            const allowed = getDecorSurfacePlaceableCells(draft);
            if (!allowed.some((c) => c.x === grid.x && c.y === grid.y)) return;
            const result = addDecorSurfaceCell(draft, grid.x, grid.y, surface);
            if ('error' in result) {
              statusMessage = result.error;
              statusIsError = true;
            } else {
              draft = result.circuit;
              statusMessage = decorKind === 'asphalt' ? 'Bitume ajouté.' : 'Terre ajoutée.';
              statusIsError = false;
            }
          } else if (isDecorSurfaceCell(draft, grid.x, grid.y)) {
            draft = removeDecorSurfaceAt(draft, grid.x, grid.y);
            statusMessage = '';
            statusIsError = false;
          }
          render();
          return;
        }

        if (decorTool === 'erase') {
          if (
            isGrandstandCell(draft, grid.x, grid.y) ||
            isSignCell(draft, grid.x, grid.y) ||
            isVehicleCell(draft, grid.x, grid.y)
          ) {
            draft = removePropAt(draft, grid.x, grid.y);
            propRectAnchor = null;
            propRectHover = null;
            statusMessage = '';
            statusIsError = false;
            render();
          }
          return;
        }

        if (decorKind === 'vehicle' && decorTool === 'paint') {
          if (!isPropRectAnchorCell(draft, grid.x, grid.y)) {
            statusMessage = 'Case invalide — placez le véhicule près du circuit.';
            statusIsError = true;
            render();
            return;
          }
          const result = addCircuitVehicle(draft, grid.x, grid.y, selectedVehicleSpriteId);
          if ('error' in result) {
            statusMessage = result.error;
            statusIsError = true;
          } else {
            draft = result.circuit;
            statusMessage = 'Véhicule placé.';
            statusIsError = false;
          }
          render();
          return;
        }

        if (decorKind === 'sign') {
          const existing = findSignAt(draft, grid.x, grid.y);
          if (existing && !propRectAnchor) {
            draft = updateSignText(draft, existing.id, signDraftText);
            statusMessage = 'Texte du panneau mis à jour.';
            statusIsError = false;
            render();
            return;
          }
        }

        if (!propRectAnchor) {
          if (!isPropRectAnchorCell(draft, grid.x, grid.y)) {
            statusMessage = 'Case invalide — placez le décor près du circuit.';
            statusIsError = true;
            render();
            return;
          }
          propRectAnchor = grid;
          propRectHover = null;
          statusMessage = '';
          statusIsError = false;
          render();
          return;
        }

        const result =
          decorKind === 'grandstand'
            ? addGrandstandRect(draft, propRectAnchor.x, propRectAnchor.y, grid.x, grid.y)
            : addSignRect(draft, propRectAnchor.x, propRectAnchor.y, grid.x, grid.y, signDraftText);

        propRectAnchor = null;
        propRectHover = null;
        if ('error' in result) {
          statusMessage = result.error;
          statusIsError = true;
        } else {
          draft = result.circuit;
          statusMessage = decorKind === 'grandstand' ? 'Gradins ajoutés.' : 'Panneau ajouté.';
          statusIsError = false;
        }
        render();
        return;
      }
    });
  };

  const persist = async (asCopy: boolean): Promise<void> => {
    const finalized = finalizeEditedCircuit(draft);
    const validation = validateManualCircuitEdit(finalized);
    if (!validation.isValid) {
      statusMessage = validation.errors.join(' ');
      statusIsError = true;
      render();
      return;
    }

    saving = true;
    statusMessage = '';
    render();

    try {
      const segments = countPathSegmentTypes(finalized.path);
      const params = {
        ...saved.params,
        numStraights: segments.straights,
        numTurns: segments.turns,
        approximateCells: finalized.path.length,
      };
      const result =
        saved.isNew || asCopy
          ? await saveCircuitCopy(finalized, params)
          : await updateCircuit(saved.id, finalized, params);
      handlers.onSaved(result);
    } catch (err) {
      statusMessage = err instanceof Error ? err.message : 'Échec de l’enregistrement.';
      statusIsError = true;
      saving = false;
      render();
    }
  };

  container.innerHTML = '';
  container.appendChild(section);
  void preloadCircuitVehicleSprites().then(() => render());
  render();
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}
